import { useCallback, useEffect, useState } from "react";

import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { allTrackers } from "app/services/track/track";
import { EAssetType } from "app/UtilitiesAndConstants/types/assetType";

import { useBuilderContext } from "../builderContext";
import type { IDomain, IDomains } from "../MarketBuilder.types";
import { getMissingDomainFavicons } from "../services/DomainsDataFetcher";

export const useDomainSelection = (entities: IDomain[]) => {
    const [selectedEntities, setSelectedEntities] = useState<IDomains>(entities);
    const [shouldSyncDomainFavicons, setShouldSyncDomainFavicons] = useState(true);
    const [serializedSuggestedData, setSerializedSuggestedData] = useState<IDomains>([]);
    const { showSegmentsDisclaimer, setShowSegmentsDisclaimer } = useBuilderContext();
    /**
     * In case any user domains were pasted/imported
     * then we should fetch the favicons for these domains.
     * this hook makes sure that all domains with missing favicons will receive the required data
     * a few short moments after being added to the list.
     */
    useEffect(() => {
        if (!shouldSyncDomainFavicons) {
            return;
        }

        async function syncUserDomainFavicons() {
            if (selectedEntities.length === 0) {
                return;
            }
            const missingFavicons = await getMissingDomainFavicons(selectedEntities);

            // Use functional update to always operate on the CURRENT state,
            // not the stale closure value from when the async operation started
            setSelectedEntities((currentEntities) =>
                currentEntities.map((entity) => ({
                    ...entity,
                    favicon: entity.favicon || missingFavicons[entity.domain],
                })),
            );
            setShouldSyncDomainFavicons(false);
        }

        void syncUserDomainFavicons();
    }, [selectedEntities, shouldSyncDomainFavicons]);

    const selectEntities = useCallback((entityToAdd: IDomains, shouldSyncFavicons = false) => {
        setSelectedEntities((currentEntities) => {
            if (MAX_DOMAINS_IN_CATEGORY === currentEntities.length) {
                return currentEntities;
            }
            return entityToAdd.slice(0, MAX_DOMAINS_IN_CATEGORY);
        });

        if (shouldSyncFavicons) {
            setShouldSyncDomainFavicons(shouldSyncFavicons);
        }
    }, []);

    const isEntitiesContainSegment = (entities) => {
        return entities.some((entity) => {
            return entity.type === EAssetType.Segment;
        });
    };

    const removeEntity = useCallback(
        (entityToRemove: IDomain) => {
            allTrackers.trackEvent(
                "Button",
                "click",
                `by ${entityToRemove.section}/Mainlist/removeDomain/${entityToRemove.domain}`,
            );

            const filterId = entityToRemove.type === EAssetType.Segment ? "id" : "domain";

            setSelectedEntities((currentEntities) => {
                const updatedEntities: IDomain[] = currentEntities.filter(
                    (domain) => domain[filterId] !== entityToRemove[filterId],
                );

                if (currentEntities.length === 1) {
                    setSerializedSuggestedData([]); // if the list is going to be empty we will clear all suggestions
                }

                if (showSegmentsDisclaimer && !isEntitiesContainSegment(updatedEntities)) {
                    setShowSegmentsDisclaimer(false);
                }

                return updatedEntities;
            });
        },
        [showSegmentsDisclaimer, setShowSegmentsDisclaimer],
    );

    const updateSuggestedEntities = useCallback((entities: IDomains) => {
        setSerializedSuggestedData(entities);
    }, []);

    /**
     * Drop the whole manual list, along with the suggestions derived from it.
     *
     * Not `selectEntities([])`: that keeps the current list untouched once it has reached
     * MAX_DOMAINS_IN_CATEGORY, which is exactly when discarding it matters most.
     */
    const clearEntities = useCallback(() => {
        setSelectedEntities([]);
        setSerializedSuggestedData([]);
        setShowSegmentsDisclaimer(false);
    }, [setShowSegmentsDisclaimer]);

    return {
        selectedEntities,
        selectEntities,
        clearEntities,
        removeEntity,
        updateSuggestedEntities,
        serializedSuggestedData,
    };
};
